import type { CSSProperties } from "react";

/**
 * A surface built on the colour of a picture: a scenario's page on its cover,
 * a map entity's card on its emblem.
 *
 * The colour is sampled when the picture is published and carried as hue and
 * strength, so a surface is right from the first frame -- before the picture
 * has downloaded, and on a skeleton that has nothing else on it yet. The
 * lightness is the theme's, and it is the one thing that has to move between
 * light and dark for text to stay readable.
 */

/** A picture's colour: degrees around the OKLCH wheel, and how much of it there is, 0 to 1. */
export interface Tint {
  hue: number;
  chroma: number;
}

/** The two properties every surface below is derived from. */
export function tintProperties(tint: Tint | null | undefined): CSSProperties {
  return {
    "--tint-hue": `${(tint?.hue ?? 0).toFixed(1)}deg`,
    "--tint-chroma": (tint?.chroma ?? 0).toFixed(3),
  } as CSSProperties;
}

/**
 * The picture's colour at a given strength, over whatever it is painted on.
 * Mixed in oklab so the hue stays where it was: a plain sRGB mix takes
 * saturated colours through grey on their way to the background.
 */
export function ground(strength: number, over = "var(--screen)"): string {
  const tint =
    "oklch(var(--tint-lightness) calc(var(--tint-max-chroma) * var(--tint-chroma)) var(--tint-hue))";
  if (strength >= 100) return tint;
  return `color-mix(in oklab, ${tint} ${strength}%, ${over})`;
}
